import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Bell, Trash2, TrendingDown } from 'lucide-react';
import { getPriceAlerts, deletePriceAlert } from '../api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const PriceAlerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchAlerts();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchAlerts = async () => {
    try {
      const response = await getPriceAlerts();
      setAlerts(response.data);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (alertId) => {
    try {
      await deletePriceAlert(alertId);
      setAlerts(alerts.filter((a) => a.alert.id !== alertId));
      toast.success('Alert deleted');
    } catch (error) {
      toast.error('Failed to delete alert');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <div className="text-center">
          <Bell size={64} className="text-white/20 mx-auto mb-6" />
          <h1 className="font-display text-4xl uppercase mb-4">PRICE ALERTS</h1>
          <p className="text-white/60 mb-8">Sign in to manage your price alerts</p>
          <Link to="/login">
            <Button className="btn-primary">SIGN IN</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-12 px-6 lg:px-12" data-testid="alerts-page">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="font-display text-5xl sm:text-6xl uppercase mb-4">PRICE ALERTS</h1>
          <p className="text-white/60">{alerts.length} active alerts</p>
        </motion.div>

        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-24 skeleton" />
            ))}
          </div>
        ) : alerts.length === 0 ? (
          <div className="text-center py-24">
            <Bell size={64} className="text-white/20 mx-auto mb-6" />
            <p className="text-white/60 text-lg mb-4">No price alerts set</p>
            <p className="text-white/40 text-sm mb-8">
              Browse products and set alerts to get notified when prices drop!
            </p>
            <Link to="/shop">
              <Button className="btn-primary">BROWSE PRODUCTS</Button>
            </Link>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            {alerts.map(({ alert, product }) => {
              const priceDiff = product.price - alert.target_price;
              const isTriggered = product.price <= alert.target_price;

              return (
                <div
                  key={alert.id}
                  className={`bg-[#0A0A0A] border-l-2 ${isTriggered ? 'border-[#D2FF00]' : 'border-white/20'} p-6 flex items-center gap-6`}
                  data-testid={`alert-${alert.id}`}
                >
                  <Link to={`/product/${product.id}`} className="flex-shrink-0">
                    <div className="w-20 h-20 bg-[#121212]">
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </Link>

                  <div className="flex-1 min-w-0">
                    <p className="text-white/40 text-xs uppercase">{product.brand}</p>
                    <Link to={`/product/${product.id}`}>
                      <h3 className="font-medium truncate hover:text-[#D2FF00] transition-colors">
                        {product.name}
                      </h3>
                    </Link>
                    <div className="flex items-center gap-4 mt-2">
                      <div>
                        <span className="text-white/40 text-xs">Current</span>
                        <p className="text-white">${product.price.toFixed(2)}</p>
                      </div>
                      <TrendingDown size={16} className="text-white/40" />
                      <div>
                        <span className="text-white/40 text-xs">Target</span>
                        <p className="text-[#D2FF00]">${alert.target_price.toFixed(2)}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex-shrink-0 text-right">
                    {isTriggered ? (
                      <span className="tag-limited mb-2 inline-block">PRICE DROPPED!</span>
                    ) : (
                      <p className="text-white/40 text-sm mb-2">
                        ${priceDiff.toFixed(2)} to go
                      </p>
                    )}
                    <button
                      onClick={() => handleDelete(alert.id)}
                      className="p-2 bg-white/10 text-white hover:bg-[#FF3B30] transition-colors"
                      title="Delete alert"
                      data-testid={`delete-alert-${alert.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default PriceAlerts;
